// Field photos on an asset (2026-10-09): what crews captured against a pipe
// (or on a project holding it), for the desktop pipe details flyout
// (assetDetails.ts) and the field app's asset card (fieldApp.ts).
//
// One server route, GET /api/field/assets/{id}/photos (routers/field.py):
// client-scoped, same visibility as /api/field/captures (a non-reviewer sees
// their own captures plus project-linked ones), project half dropped for a
// GIS-view organisation. Photos shown here are ALWAYS the blurred shared
// copies (thumb / shared: faces and number plates blurred, EXIF stripped);
// the unblurred original is only ever a separate, explicit link, offered
// when the server says the caller may see it (capturer + reviewers).
import { getJson } from "../api";
import "../styles/assetPhotos.css";
import { fill, fmtDate, h } from "./dom";
import type { Capture, CapturePhoto } from "./fieldApi";
import { photoUrl } from "./fieldApi";

export interface AssetPhotoCapture extends Capture {
  via_project?: { id: string; name: string };
  project_name?: string | null;
}

export interface AssetPhotos {
  asset_id: string;
  captures: AssetPhotoCapture[];
  project_captures: AssetPhotoCapture[];
  projects: { id: string; name: string; status: string | null }[];
  n_captures: number;
  n_photos: number;
  is_reviewer: boolean;
  /** form kind -> its label (config/field_taxonomy.yaml) */
  form_labels?: Record<string, string>;
}

export interface AssetPhotoOptions {
  /** field app card: one horizontal strip, no per-capture rows */
  compact?: boolean;
  /** display name for "who took it" (presentation-mode staff masking) */
  person?: (c: AssetPhotoCapture) => string;
  /** the form's label (field config), else the raw form kind */
  formLabel?: (kind: string) => string;
  /** an "open this capture" action shown in the viewer, or null */
  openCapture?: (c: AssetPhotoCapture) => { label: string; run: () => void } | null;
}

const MAX_AGE_MS = 60_000;
const cache = new Map<string, { at: number; p: Promise<AssetPhotos> }>();

/** Cached for a minute: the field app re-renders its card on every GPS
 * update, and the desktop flyout may be reopened on the same pipe. */
export function fetchAssetPhotos(assetId: string, force = false): Promise<AssetPhotos> {
  const hit = cache.get(assetId);
  if (!force && hit && Date.now() - hit.at < MAX_AGE_MS) return hit.p;
  const p = getJson<AssetPhotos>(`/api/field/assets/${encodeURIComponent(assetId)}/photos`);
  cache.set(assetId, { at: Date.now(), p });
  p.catch(() => cache.delete(assetId));
  return p;
}

const STATUS_LABEL: Record<string, string> = {
  submitted: "Awaiting review",
  reviewed: "Reviewed",
  actioned: "Actioned",
  closed: "Closed",
  rejected: "Rejected",
};

function who(c: AssetPhotoCapture, o: AssetPhotoOptions): string {
  return (o.person ? o.person(c) : c.user_name) || "Unknown";
}

function form(c: AssetPhotoCapture, o: AssetPhotoOptions): string {
  return o.formLabel ? o.formLabel(c.form_kind) : c.form_kind.replace(/_/g, " ");
}

interface Slide {
  c: AssetPhotoCapture;
  p: CapturePhoto;
}

function slidesOf(caps: AssetPhotoCapture[]): Slide[] {
  return caps.flatMap((c) => c.photos.filter((p) => p.processed).map((p) => ({ c, p })));
}

function statusBadge(status: string): HTMLElement {
  return h("span", { class: `aph-status aph-st-${status}` }, STATUS_LABEL[status] ?? status);
}

function pendingTile(p: CapturePhoto): HTMLElement {
  return h("span", { class: "aph-pending", title: p.error ? "Could not blur this photo - a reviewer must re-run it" : "Processing (privacy blur)" },
    p.error ? "!" : "…");
}

/** The section body: captures newest first (photos open the viewer), then
 * captures on the asset's project(s), labelled. */
export function renderAssetPhotos(data: AssetPhotos, opts: AssetPhotoOptions = {}): HTMLElement {
  const labels = data.form_labels ?? {};
  const o: AssetPhotoOptions = { formLabel: (k) => labels[k] ?? k.replace(/_/g, " "), ...opts };
  const all = [...data.captures, ...data.project_captures];
  const slides = slidesOf(all);
  const open = (c: AssetPhotoCapture, p: CapturePhoto) => {
    const i = slides.findIndex((s) => s.c.id === c.id && s.p.id === p.id);
    openPhotoViewer(slides, Math.max(0, i), o);
  };
  const thumb = (c: AssetPhotoCapture, p: CapturePhoto) =>
    p.processed
      ? h("button", { type: "button", class: "aph-thumb", title: `${form(c, o)} · ${fmtDate(c.captured_at ?? c.received_at)}`,
          onclick: () => open(c, p) },
          h("img", { src: photoUrl(c, p, "thumb"), alt: `Field photo, ${form(c, o)}`, loading: "lazy" }),
          c.via_project ? h("span", { class: "aph-tag" }, "Project") : null)
      : pendingTile(p);

  if (!all.length) {
    return h("div", { class: `aph${o.compact ? " aph-compact" : ""}` },
      h("p", { class: "aph-empty" }, "No field photos for this asset yet."));
  }

  if (o.compact) {
    const latest = all[0];
    return h("div", { class: "aph aph-compact" },
      h("div", { class: "aph-strip" }, ...all.flatMap((c) => c.photos.map((p) => thumb(c, p)))),
      h("small", { class: "aph-meta" },
        `${data.n_captures} capture${data.n_captures === 1 ? "" : "s"} · latest ${fmtDate(latest.captured_at ?? latest.received_at)}` +
        ` · ${form(latest, o)} · ${who(latest, o)}`),
      data.project_captures.length
        ? h("small", { class: "aph-meta" }, `"Project" = taken on ${data.projects.map((p) => p.name).join(", ")}, not picked against this asset.`)
        : null);
  }

  const row = (c: AssetPhotoCapture) =>
    h("div", { class: "aph-cap" },
      h("div", { class: "aph-strip" }, ...(c.photos.length ? c.photos.map((p) => thumb(c, p)) : [h("span", { class: "aph-pending" }, "No photo")])),
      h("div", { class: "aph-line" },
        h("strong", {}, form(c, o)), " · ", fmtDate(c.captured_at ?? c.received_at), " · ", who(c, o), " ", statusBadge(c.status)),
      c.notes ? h("div", { class: "aph-note" }, c.notes) : null,
      c.review_note ? h("div", { class: "aph-note aph-dim" }, `Review note: ${c.review_note}`) : null);

  const out = h("div", { class: "aph" });
  if (data.captures.length) out.append(...data.captures.map(row));
  else out.append(h("p", { class: "aph-empty" }, "No captures picked against this asset."));
  if (data.project_captures.length) {
    out.append(h("div", { class: "aph-group" },
      `On this asset's project${data.projects.length === 1 ? "" : "s"}: ${data.projects.map((p) => p.name).join(", ")}`),
      h("p", { class: "aph-dim aph-small" }, "Taken on the project, not picked against this pipe - may show nearby work."),
      ...data.project_captures.map(row));
  }
  out.append(h("p", { class: "aph-dim aph-small" },
    "Photos are the shared copies: location data stripped, faces and number plates blurred automatically."));
  return out;
}

// --- viewer --------------------------------------------------------------

let activeViewer: HTMLElement | null = null;
let viewerKeys: ((e: KeyboardEvent) => void) | null = null;

export function closePhotoViewer(): void {
  activeViewer?.remove();
  activeViewer = null;
  if (viewerKeys) window.removeEventListener("keydown", viewerKeys, true);
  viewerKeys = null;
}

/** Full-screen viewer over the blurred shared copies, with prev / next
 * (buttons, arrow keys, swipe) and links: full size, the original when the
 * caller may see it, and the caller's "open capture" action. */
export function openPhotoViewer(slides: Slide[], start: number, o: AssetPhotoOptions = {}): void {
  closePhotoViewer();
  if (!slides.length) return;
  let i = Math.min(Math.max(start, 0), slides.length - 1);
  const img = h("img", { class: "aph-v-img", alt: "" });
  const caption = h("div", { class: "aph-v-caption" });
  const counter = h("span", { class: "aph-v-count" });
  const prev = h("button", { type: "button", class: "aph-v-nav aph-v-prev", "aria-label": "Previous photo", onclick: () => go(-1) }, "‹");
  const next = h("button", { type: "button", class: "aph-v-nav aph-v-next", "aria-label": "Next photo", onclick: () => go(1) }, "›");
  const close = h("button", { type: "button", class: "aph-v-close", "aria-label": "Close", onclick: () => closePhotoViewer() }, "✕");
  const overlay = h("div", { class: "aph-viewer", role: "dialog", "aria-modal": "true", "aria-label": "Field photo" },
    h("div", { class: "aph-v-top" }, counter, close),
    h("div", { class: "aph-v-stage" }, prev, img, next),
    caption);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay || (e.target as HTMLElement).classList.contains("aph-v-stage")) closePhotoViewer();
  });
  let touchX: number | null = null;
  overlay.addEventListener("touchstart", (e) => { touchX = e.touches[0]?.clientX ?? null; }, { passive: true });
  overlay.addEventListener("touchend", (e) => {
    const x = e.changedTouches[0]?.clientX;
    if (touchX !== null && x !== undefined && Math.abs(x - touchX) > 50) go(x < touchX ? 1 : -1);
    touchX = null;
  });

  function render(): void {
    const { c, p } = slides[i];
    img.src = photoUrl(c, p, "shared");
    img.alt = `Field photo ${i + 1} of ${slides.length}`;
    counter.textContent = `${i + 1} / ${slides.length}`;
    prev.disabled = i === 0;
    next.disabled = i === slides.length - 1;
    const links: (HTMLElement | null)[] = [
      h("a", { href: photoUrl(c, p, "shared"), target: "_blank", rel: "noopener" }, "Open full size"),
      c.can_see_original
        ? h("a", { href: photoUrl(c, p, "original"), target: "_blank", rel: "noopener",
            title: "The unblurred original - visible to the capturer and reviewers only. Don't share it." }, "Original (unblurred)")
        : null,
    ];
    const act = o.openCapture?.(c);
    if (act) links.push(h("button", { type: "button", class: "aph-v-link", onclick: () => { closePhotoViewer(); act.run(); } }, act.label));
    fill(caption,
      h("div", {}, h("strong", {}, `Capture #${c.id} · ${form(c, o)}`), " ", statusBadge(c.status)),
      h("div", {}, `${fmtDate(c.captured_at ?? c.received_at)} · ${who(c, o)}${c.asset_id ? ` · ${c.asset_id}` : ""}`),
      c.via_project ? h("div", { class: "aph-dim" }, `On project ${c.via_project.name} (not picked against this asset)`) : null,
      c.notes ? h("div", { class: "aph-note" }, c.notes) : null,
      h("div", { class: "aph-v-links" }, ...links.filter((x): x is HTMLElement => x !== null)),
    );
  }
  function go(d: number): void {
    const n = i + d;
    if (n < 0 || n >= slides.length) return;
    i = n;
    render();
  }

  // capture phase + stopPropagation: Escape closes the viewer only, never
  // the details flyout underneath (its own document-level Escape handler)
  viewerKeys = (e: KeyboardEvent) => {
    if (e.key === "Escape") closePhotoViewer();
    else if (e.key === "ArrowLeft") go(-1);
    else if (e.key === "ArrowRight") go(1);
    else return;
    e.stopPropagation();
    e.preventDefault();
  };
  window.addEventListener("keydown", viewerKeys, true);
  render();
  document.body.append(overlay);
  activeViewer = overlay;
  close.focus();
}

/** A collapsible "Field photos" section: the count badge fills in from a
 * small JSON fetch, the thumbnails are only built (and loaded) when it's
 * opened. Used by the desktop details flyout. */
export function assetPhotosSection(assetId: string, o: AssetPhotoOptions = {}): HTMLElement {
  const badge = h("span", { class: "aph-badge" }, "…");
  const body = h("div", { class: "aph-body" });
  const det = h("details", { class: "aph-section" },
    h("summary", {}, "Field photos ", badge),
    body);
  let data: AssetPhotos | null = null;
  let built = false;
  const build = () => {
    if (built || !det.open) return;
    if (!data) {
      body.replaceChildren(h("p", { class: "aph-empty" }, "Loading…"));
      return;
    }
    built = true;
    body.replaceChildren(renderAssetPhotos(data, o));
  };
  det.addEventListener("toggle", build);
  fetchAssetPhotos(assetId)
    .then((d) => {
      data = d;
      badge.textContent = String(d.n_photos);
      badge.title = `${d.n_photos} photo(s) in ${d.n_captures} capture(s)`;
      badge.classList.toggle("aph-badge-zero", d.n_photos === 0 && d.n_captures === 0);
      if (d.n_captures === 0) badge.textContent = "0";
      build();
    })
    .catch(() => {
      badge.textContent = "–";
      badge.title = "Field photos unavailable";
      body.replaceChildren(h("p", { class: "aph-empty" }, "Field photos aren't available for this asset (or to your organisation)."));
      built = true;
    });
  return det;
}
